// Grand Quack Auto — cop pursuit system (beat + riot variants, flanking, swat AOE).
import * as THREE from 'three';
import { COP, PALETTE } from '../config';
import { triggerDeathScreen, isDeathActive } from '../ui/deathScreen';
import { playCrashSound, startSiren, stopSiren } from '../utils/audio';
import { pushOutOfBuildings } from '../utils/buildingCollision';
import { addChaos } from './chaosSystem';
import { isEvolving, getLastEvolvedAt } from './duckEvolutionSystem';
import { triggerShake, triggerSlowMo } from './juiceSystem';
import { getDucklings, killDuckling } from './ducklingSystem';
import type { System, World } from '../types';

export type CopKind = 'beat' | 'riot';

export interface CopEntity {
  group: THREE.Group;
  kind: CopKind;
  speed: number;
  pushResist: number;       // remaining pushes before next stagger
  staggerSeconds: number;
  staggerUntil: number;
  swatCooldownUntil: number;
  swatWindupStartedAt: number; // -1 when not winding up
}

const cops: CopEntity[] = [];
let lastSpawnCheck = 0;

export function getCops(): CopEntity[] { return cops; }

// Shared materials + geometries — reuse across all cops (Three.js best practice).
const copMat: Record<CopKind, THREE.MeshToonMaterial> = { beat: new THREE.MeshToonMaterial({ color: COP.kinds.beat.bodyColor }), riot: new THREE.MeshToonMaterial({ color: COP.kinds.riot.bodyColor }) };
const shieldMat = new THREE.MeshToonMaterial({ color: 0x222233 });
const bodyGeom: Record<CopKind, THREE.CylinderGeometry> = { beat: new THREE.CylinderGeometry(COP.kinds.beat.bodyRadius, COP.kinds.beat.bodyRadius + 0.04, 0.8, 8), riot: new THREE.CylinderGeometry(COP.kinds.riot.bodyRadius, COP.kinds.riot.bodyRadius + 0.04, 0.8, 8) };
const headGeom = new THREE.SphereGeometry(0.14, 8, 6);
const shieldGeom = new THREE.BoxGeometry(0.55, 0.7, 0.04);

function buildCopVisual(kind: CopKind): THREE.Group {
  const group = new THREE.Group();
  group.name = `cop-${kind}`;
  const mat = copMat[kind];

  const body = new THREE.Mesh(bodyGeom[kind], mat);
  body.position.y = 0.4;
  body.castShadow = true;
  group.add(body);

  const head = new THREE.Mesh(headGeom, mat);
  head.position.y = 0.92;
  head.castShadow = true;
  group.add(head);

  if (kind === 'riot') {
    const shield = new THREE.Mesh(shieldGeom, shieldMat);
    shield.position.set(0, 0.5, 0.32);
    shield.castShadow = true;
    group.add(shield);
  }
  return group;
}

function spawnCop(world: World, kind: CopKind): void {
  const cfg = COP.kinds[kind];
  const group = buildCopVisual(kind);
  const angle = Math.random() * Math.PI * 2;
  const duck = world.duck.position;
  group.position.set(
    duck.x + Math.cos(angle) * COP.spawnDistance,
    0,
    duck.z + Math.sin(angle) * COP.spawnDistance,
  );
  world.scene.add(group);
  cops.push({
    group, kind,
    speed: cfg.speed,
    pushResist: cfg.pushResist,
    staggerSeconds: cfg.staggerSeconds,
    staggerUntil: 0,
    swatCooldownUntil: 0,
    swatWindupStartedAt: -1,
  });
}

const _dir = new THREE.Vector3();
const _flankTarget = new THREE.Vector3();

function flankOffset(idx: number, count: number, out: THREE.Vector3): void {
  const angle = (idx / Math.max(1, count)) * Math.PI * 2;
  out.set(Math.cos(angle) * COP.flankRadius, 0, Math.sin(angle) * COP.flankRadius);
}

function countSwarmers(cop: CopEntity): number {
  let n = 0;
  const ducklings = getDucklings();
  for (const d of ducklings) {
    if (d.mode !== 'defending') continue;
    if (cop.group.position.distanceTo(d.group.position) < COP.swatRadius * 1.4) n++;
  }
  return n;
}

function performSwat(world: World, cop: CopEntity): void {
  const ducklings = getDucklings();
  // Snapshot then kill — killDuckling mutates the brood array.
  const victims: ReturnType<typeof getDucklings>[number][] = [];
  for (const d of ducklings) {
    if (cop.group.position.distanceTo(d.group.position) < COP.swatRadius) victims.push(d);
  }
  for (const v of victims) killDuckling(world, v);
}

export const copSystem: System = {
  name: 'cop',

  fixedUpdate(dt: number, world: World) {
    const cap = COP.maxCopsByWanted[world.chaos.wanted] ?? 0;

    // Spawn check (throttled to 1/s).
    if (world.time.elapsed - lastSpawnCheck >= 1) {
      lastSpawnCheck = world.time.elapsed;
      if (world.chaos.wanted >= 1 && cops.length < cap) {
        const useRiot = world.chaos.wanted >= 2 && Math.random() < COP.riotChanceAtMenace;
        spawnCop(world, useRiot ? 'riot' : 'beat');
        if (cops.length === 1) startSiren();
      }
      while (cops.length > cap) { world.scene.remove(cops.pop()!.group); }
      if (cap === 0 && cops.length === 0) stopSiren();
    }

    const target = world.playerMode === 'driving' && world.activeVehicle
      ? world.activeVehicle.group.position
      : world.duck.position;

    for (let i = 0; i < cops.length; i++) {
      const cop = cops[i]!;

      // Swat windup: while winding up, cop stops moving and "raises arms".
      // Cancel if the brood dies mid-windup so swat doesn't fire on nothing.
      if (cop.swatWindupStartedAt >= 0) {
        if (countSwarmers(cop) < COP.swatTriggerCount) {
          cop.swatWindupStartedAt = -1;
          cop.group.children[0]!.rotation.x = 0;
        } else {
          const t = world.time.elapsed - cop.swatWindupStartedAt;
          cop.group.children[0]!.rotation.x = -Math.min(t / COP.swatWindupSeconds, 1) * 0.6;
          if (t >= COP.swatWindupSeconds) {
            cop.swatWindupStartedAt = -1;
            cop.group.children[0]!.rotation.x = 0;
            cop.swatCooldownUntil = world.time.elapsed + COP.swatCooldownSeconds;
            performSwat(world, cop);
          }
        }
        continue;
      }

      // Trigger swat if mobbed and off cooldown.
      if (world.time.elapsed >= cop.swatCooldownUntil
          && countSwarmers(cop) >= COP.swatTriggerCount) {
        cop.swatWindupStartedAt = world.time.elapsed;
        continue;
      }

      // Stagger: frozen by a duckling shove. pushResist is managed by
      // ducklingSystem on each push — we just freeze here until it expires.
      if (world.time.elapsed < cop.staggerUntil) continue;

      // Flanking spread until close, then dead-on chase.
      flankOffset(i, cops.length, _flankTarget);
      _flankTarget.add(target);
      const directDist = cop.group.position.distanceTo(target);
      const aim = directDist < COP.flankRadius ? target : _flankTarget;

      _dir.copy(aim).sub(cop.group.position);
      _dir.y = 0;
      const dist = _dir.length();
      if (dist > 0.3) {
        _dir.normalize();
        cop.group.position.addScaledVector(_dir, cop.speed * dt);
        cop.group.rotation.y = Math.atan2(_dir.x, _dir.z);
        // Push cops out of buildings (no physics body, same as ducklings).
        const fixed = pushOutOfBuildings(cop.group.position.x, cop.group.position.z);
        cop.group.position.x = fixed.x;
        cop.group.position.z = fixed.z;
      }

      // Bump detection vs the player.
      // Skip during evolution cinematic and 2s grace period after.
      const EVO_GRACE = 2.0;
      const evoImmune = isEvolving() || (world.time.elapsed - getLastEvolvedAt() < EVO_GRACE);
      if (directDist < 1.0 && !isDeathActive() && !evoImmune) {
        addChaos(world, 'copHit');
        triggerShake(0.5);
        triggerSlowMo(world);
        playCrashSound();
        if (Math.random() < 0.1) triggerDeathScreen(world);
      }
    }
  },
};
